import * as Yup from "yup";
import InputFormik from "../../../../common/components/form/InputFormik";
import { maskCNPJ, maskPhone } from "../../../../common/helpers/mask.helpers";
import { useFormik, FormikProvider } from "formik";
import AddressFields from "../../../renters/components/forms/fields/AddressFields";
import ButtonWithLoader from "../../../../common/components/ui/loading/ButtonLoading";
import {
  emptyCompany,
  ICompany,
  Provider,
  PROVIDERS_TOKEN,
} from "../../../../types/company.type";
import { Button } from "../../../../components/ui/button";
import { TrashIcon } from "lucide-react";
import ComboBoxFormik from "@/common/components/form/ComboBoxFormik";
import { LeadGoalsField } from "./LeadsGoalsField";
import { useState } from "react";

type FormPropsEditCompany = {
  onSubmit: (
    values: ICompany
  ) => Promise<{ ok: boolean; data: ICompany; errors?: unknown[] }>;
  data: ICompany;
  formRequestErrors?: { message: string; path: string[] }[];
};

const providerOptions = PROVIDERS_TOKEN.map((provider: Provider) => ({
  value: provider,
  label: provider.charAt(0).toUpperCase() + provider.slice(1),
}));

const validationSchema = Yup.object().shape({
  socialname: Yup.string().required("Nome obrigatório"),
  fantasyname: Yup.string().required("Nome fantasia obrigatório"),
  cnpj: Yup.string().required("CNPJ obrigatório"),
  email: Yup.string().email("Email inválido").required("Email obrigatório"),
  phone: Yup.string().required("Telefone obrigatório"),
  active: Yup.boolean(),
  address: Yup.object().shape({
    zipcode: Yup.string().required("CEP é obrigatório"),
    street: Yup.string().required("Rua é obrigatória"),
    number: Yup.string().required("Número é obrigatório"),
    complement: Yup.string(),
    state: Yup.string().required("Estado é obrigatório"),
    city: Yup.string().required("Cidade é obrigatória"),
    district: Yup.string().required("Bairro é obrigatório"),
    acronym: Yup.string().max(3, "Máximo de 3 letras"),
  }),
  contacts: Yup.object().shape({
    operational: Yup.object().shape({
      name: Yup.string(),
      whatsapp: Yup.string(),
    }),
    commercial: Yup.object().shape({
      name: Yup.string(),
      whatsapp: Yup.string(),
    }),
  }),
  leadGoals: Yup.array().of(
    Yup.object().shape({
      hour: Yup.string(),
      goalQuantity: Yup.number(),
    })
  ),
  accessTokens: Yup.array().of(
    Yup.object().shape({
      provider: Yup.string(),
      token: Yup.string(),
    })
  ),
});

function FormEditCompany(props: FormPropsEditCompany) {
  const [tempToken, setTempToken] = useState<{
    provider: Provider;
    token: string;
  }>({ provider: "" as Provider, token: "" });

  const formik = useFormik({
    initialValues: {
      ...(props.data
        ? {
            ...emptyCompany(),
            ...props.data,
            leadGoals: props.data.leadGoals || [],
            contacts: props.data.contacts || {
              operational: { name: "", whatsapp: "" },
              commercial: { name: "", whatsapp: "" },
            },
          }
        : emptyCompany()),
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      const result = await props.onSubmit(values as ICompany);
      if (result.ok) {
        formik.setValues({
          ...result.data,
        });
      }
      return result;
    },
  });

  const handleAddToken = () => {
    if (!tempToken.provider || !tempToken.token) return;

    formik.setFieldValue("accessTokens", [
      ...(formik.values.accessTokens || []),
      tempToken,
    ]);

    setTempToken({ provider: "" as Provider, token: "" });
    formik.setFieldValue("tempToken", "");
    formik.setFieldValue("tempProvider", "");
  };

  const handleRemoveToken = (index: number) => {
    const newTokens = formik.values.accessTokens.filter((_, i) => i !== index);
    formik.setFieldValue("accessTokens", newTokens);
  };

  const maskToken = (token: string) => {
    if (token.length <= 8) {
      return "****";
    }
    return `****${token.slice(-4)}`;
  };

  return (
    <form onSubmit={formik.handleSubmit}>
      <FormikProvider value={formik}>
        <section className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-semibold mb-4">
            Informações da Empresa
          </h2>
          <div className="grid grid-cols-3 gap-2 mb-4">
            <div className="grid grid-cols-1 text-sm lg:text-lg col-span-3 gap-2 sm:grid-cols-2 xl:grid-cols-3">
              <InputFormik
                label="Nome da Empresa:"
                name="socialname"
                className=""
                required={true}
              />
              <InputFormik
                label="Nome Fantasia:"
                name="fantasyname"
                className=""
                required={true}
              />
              <InputFormik
                label="CNPJ:"
                name="cnpj"
                placeholder="11.222.333/0001-00"
                required={true}
                mask={maskCNPJ}
              />

              <InputFormik
                label="Telefone:"
                name="phone"
                type="whatsappComercial"
                required={true}
                mask={maskPhone}
              />
              <InputFormik
                label="E-mail:"
                name="email"
                type="email"
                required={true}
              />
            </div>
          </div>
          <div className="flex items-center gap-2 mt-4 mb-6">
            <input
              type="checkbox"
              id="active"
              name="active"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              checked={formik.values.active}
              className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <label
              htmlFor="active"
              className="text-sm font-medium text-gray-900"
            >
              Cidade Ativa
            </label>
          </div>
          <section className="bg-gray2/20 mt-6 mb-10 p-3 rounded-md shadow">
            <h2 className="text-2xl font-semibold mb-4">Endereço</h2>
            <AddressFields />
          </section>

          <section className="bg-gray2/20 mt-6 mb-10 p-3 rounded-md shadow">
            <h2 className="text-2xl font-semibold mb-4">Cargas tributárias</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <InputFormik
                label="PIS (%):"
                name="taxBurdens.PIS"
                type="number"
              />
              <InputFormik
                label="COFINS (%):"
                name="taxBurdens.COFINS"
                type="number"
              />
              <InputFormik
                label="IRPJ (%):"
                name="taxBurdens.IRPJ"
                type="number"
              />
              <InputFormik
                label="ADIC (%):"
                name="taxBurdens.ADIC"
                type="number"
              />
              <InputFormik
                label="CSLL (%):"
                name="taxBurdens.CSLL"
                type="number"
              />
              <InputFormik
                label="ISS (%):"
                name="taxBurdens.ISS"
                type="number"
              />
            </div>
          </section>

          <LeadGoalsField />
          <section className="bg-gray2/20 mt-6 mb-10 p-3 rounded-md shadow">
            <h2 className="text-2xl font-semibold mb-4">Tokens de Acesso</h2>

            <FormikProvider value={formik}>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
                <ComboBoxFormik
                  label="Nome do Token:"
                  name="tempProvider"
                  placeholder="Selecione o provedor"
                  options={providerOptions}
                  onChange={(value) =>
                    setTempToken((prev) => ({
                      ...prev,
                      provider: value as Provider,
                    }))
                  }
                  value={tempToken.provider || ""}
                />

                <InputFormik
                  label="Token"
                  name="tempToken"
                  placeholder="Cole o token aqui"
                  value={tempToken.token || ""}
                  onChange={(e) =>
                    setTempToken((prev) => ({ ...prev, token: e.target.value }))
                  }
                  className="mb-3"
                />

                <Button type="button" onClick={handleAddToken}>
                  Adicionar
                </Button>
              </div>
            </FormikProvider>

            <div className="mt-4 space-y-2">
              {formik.values.accessTokens &&
                formik.values.accessTokens.map((token, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-2 bg-white rounded-md shadow-sm"
                  >
                    <div>
                      <span className="font-medium">{token.provider}</span>:{" "}
                      <span className="font-mono text-sm">
                        {maskToken(token.token)}
                      </span>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      type="button"
                      onClick={() => handleRemoveToken(index)}
                    >
                      <TrashIcon className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                ))}
            </div>
          </section>
          <section className="bg-gray2/20 mt-6 mb-10 p-3 rounded-md shadow">
            <h2 className="text-2xl font-semibold mb-4">Contatos</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <h3 className="text-lg font-medium mb-2">
                  Contato Operacional
                </h3>
                <div className="space-y-3">
                  <InputFormik
                    label="Nome:"
                    name="contacts.operational.name"
                    placeholder="Nome do contato operacional"
                  />
                  <InputFormik
                    label="WhatsApp:"
                    name="contacts.operational.whatsapp"
                    placeholder="(00) 00000-0000"
                    mask={maskPhone}
                  />
                </div>
              </div>
              <div>
                <h3 className="text-lg font-medium mb-2">Contato Comercial</h3>
                <div className="space-y-3">
                  <InputFormik
                    label="Nome:"
                    name="contacts.commercial.name"
                    placeholder="Nome do contato comercial"
                  />
                  <InputFormik
                    label="WhatsApp:"
                    name="contacts.commercial.whatsapp"
                    placeholder="(00) 00000-0000"
                    mask={maskPhone}
                  />
                </div>
              </div>
            </div>
          </section>

          <div className="flex justify-end space-x-2 mt-6">
            <ButtonWithLoader
              isLoading={formik.isSubmitting}
              disabled={!formik.isValid}
              onClick={formik.handleSubmit}
            >
              Salvar
            </ButtonWithLoader>
          </div>
        </section>
      </FormikProvider>
    </form>
  );
}

export default FormEditCompany;
